// This file is part of MinIO Operator
// Copyright (c) 2022 MinIO, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <http://www.gnu.org/licenses/>.

import get from "lodash/get";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { setErrorSnackBarMessage } from "../../../systemSlice";
import { ErrorResponseHandler } from "../../../../common/types";
import { api, oprRpc } from "../../../../api";

export interface NamespaceRequest {
  siteId: string;
  namespace: string;
}

export const validateNamespaceAsync = createAsyncThunk(
  "createTenant/validateNamespaceAsync",
  async (values: NamespaceRequest, { rejectWithValue, dispatch }) => {
    const siteId: string = values.siteId;
    const namespace: string = values.namespace;

    return oprRpc.api
      .listTenants(siteId, namespace)
      .then((res): boolean => {
        const tenantsList = get(res, "data.tenants", []);
        let nsEmpty = true;
        if (tenantsList && tenantsList.length > 0) {
          return false;
        }
        if (nsEmpty) {
          dispatch(namespaceResourcesAsync(values));
        }
        // it's true it's empty
        return nsEmpty;
      })
      .catch((err) => {
        dispatch(
          setErrorSnackBarMessage({
            errorMessage: "Error validating if namespace already has tenants",
            detailedError: err.detailedError,
          }),
        );
        return rejectWithValue(false);
      });
  },
);

export const namespaceResourcesAsync = createAsyncThunk(
  "createTenant/namespaceResourcesAsync",
  async (values: NamespaceRequest, { rejectWithValue }) => {
    const siteId: string = values.siteId;
    const namespace: string = values.namespace;

    return api.sites
      .getResourceQuota(siteId, namespace, `${namespace}-storagequota`)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.error("Namespace quota error: ", err);
        return rejectWithValue(null);
      });
  },
);

export const createNamespaceAsync = createAsyncThunk(
  "createTenant/createNamespaceAsync",
  async (values: NamespaceRequest, { rejectWithValue, dispatch }) => {
    const siteId: string = values.siteId;
    const namespace: string = values.namespace;

    return api.sites
      .createNamespace(siteId, { name: namespace })
      .then((_) => {
        // revalidate the name to have the storage classes populated
        dispatch(validateNamespaceAsync(values));
        return true;
      })
      .catch((err: ErrorResponseHandler) => {
        dispatch(setErrorSnackBarMessage(err));
        rejectWithValue(false);
      });
  },
);
